import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RecordingPlayer from './RecordingPlayer';

// Full-screen (modal, with margins) recording viewer: video on the left (~60%)
// and the transcript on the right (~36%). The transcript is a speaker-labelled
// sentence timeline ("Doctor: …" / "Patient: …"); the sentence matching the
// current playback time is highlighted + auto-scrolled, and clicking a sentence
// seeks the video there. Falls back to plain text when there are no segments.
export default function RecordingModal({ open, onClose, src, transcript, segments, title }) {
    const { t } = useTranslation(['appointmentdetails']);
    const playerRef = useRef(null);
    const activeRef = useRef(null);
    const [time, setTime] = useState(0);

    // Poll the player's current time (vidstack exposes currentTime on the instance).
    useEffect(() => {
        if (!open) return;
        const id = setInterval(() => {
            const p = playerRef.current;
            const ct = p && typeof p.currentTime === 'number' ? p.currentTime : null;
            if (ct != null) setTime(ct);
        }, 200);
        return () => clearInterval(id);
    }, [open]);

    // Sentence segments [{ speaker, name, text, start, end }]. Guard against older
    // word-level payloads (which have `word` but no `text`).
    const segs = useMemo(
        () => (Array.isArray(segments) ? segments.filter((s) => s && typeof s.text === 'string') : []),
        [segments]
    );
    const hasSegments = segs.length > 0;

    const activeIdx = useMemo(() => {
        if (!hasSegments) return -1;
        let idx = -1;
        for (let i = 0; i < segs.length; i += 1) {
            if ((segs[i].start ?? 0) <= time + 0.15) idx = i; else break;
        }
        return idx;
    }, [hasSegments, segs, time]);

    // Keep the highlighted sentence in view.
    useEffect(() => {
        if (activeRef.current) activeRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [activeIdx]);

    // Close on Escape.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const seekTo = (secs) => { const p = playerRef.current; if (p && secs != null) { try { p.currentTime = secs; } catch (e) { /* noop */ } } };

    return createPortal(
        <div
            data-testid="recording-modal"
            className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-3 sm:p-6"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full h-full max-w-[95vw] max-h-[92vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-100">
                    <h3 className="text-sm sm:text-base font-bold text-[#2D8A8A]">
                        {title || t('appointmentdetails.meeting_recording', 'Meeting recording')}
                    </h3>
                    <button type="button" onClick={onClose} data-testid="recording-modal-close" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 sm:p-6 overflow-hidden">
                    {/* Left ~60% — video */}
                    <div className="lg:w-[62%] flex items-center justify-center">
                        <RecordingPlayer src={src} playerRef={playerRef} className="w-full" />
                    </div>

                    {/* Right ~36% — speaker-labelled transcript */}
                    <div className="lg:w-[36%] flex-1 flex flex-col min-h-0">
                        <div className="text-xs font-semibold text-[#3AA8A8] mb-2">
                            {t('appointmentdetails.transcript', 'Transcript')}
                        </div>
                        <div
                            data-testid="recording-modal-transcript"
                            className="flex-1 overflow-y-auto custom-scrollbar rounded-xl border border-gray-100 bg-gray-50/50 p-4 text-sm space-y-2"
                        >
                            {hasSegments ? (
                                segs.map((s, i) => {
                                    const isDoctor = (s.speaker || '').toLowerCase() === 'doctor';
                                    const isActive = i === activeIdx;
                                    return (
                                        <div
                                            key={i}
                                            ref={isActive ? activeRef : null}
                                            onClick={() => seekTo(s.start)}
                                            className={`cursor-pointer rounded-lg px-3 py-2 transition-colors ${
                                                isActive ? 'bg-[#4ABDBD]/15 ring-1 ring-[#4ABDBD]/40' : 'hover:bg-gray-100'
                                            }`}
                                        >
                                            <span className={`text-xs font-bold ${isDoctor ? 'text-[#2D8A8A]' : 'text-indigo-600'}`}>
                                                {s.name || (isDoctor ? 'Doctor' : 'Patient')}:
                                            </span>{' '}
                                            <span className={`${isActive ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                                                {s.text}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="whitespace-pre-line text-gray-700 leading-7">
                                    {transcript || t('appointmentdetails.no_transcript', 'No transcript available.')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
